import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { ArrowLeft, Printer, Target, ShieldAlert, Zap, Server, Activity, DollarSign, CheckCircle2, Layers } from 'lucide-react';

const formatCurrency = (val, country) => {
  if (!val && val !== 0) return '—';
  
  const mappings = {
    'India': { currency: 'INR', locale: 'en-IN' },
    'Singapore': { currency: 'SGD', locale: 'en-SG' },
    'Australia': { currency: 'AUD', locale: 'en-AU' },
    'New Zealand': { currency: 'NZD', locale: 'en-NZ' },
    'Japan': { currency: 'JPY', locale: 'ja-JP' },
    'South Korea': { currency: 'KRW', locale: 'ko-KR' },
    'Hong Kong': { currency: 'HKD', locale: 'en-HK' },
    'United States': { currency: 'USD', locale: 'en-US' },
    'Canada': { currency: 'CAD', locale: 'en-CA' },
    'United Kingdom': { currency: 'GBP', locale: 'en-GB' },
    'Sweden': { currency: 'SEK', locale: 'sv-SE' },
    'Switzerland': { currency: 'CHF', locale: 'de-CH' },
    'United Arab Emirates': { currency: 'AED', locale: 'en-AE' },
    'Saudi Arabia': { currency: 'SAR', locale: 'en-SA' },
    'South Africa': { currency: 'ZAR', locale: 'en-ZA' }
  };

  const euCountries = [
    'Germany', 'France', 'Netherlands', 'Ireland', 
    'Austria', 'Belgium', 'Cyprus', 'Estonia', 'Finland', 'Greece',
    'Italy', 'Latvia', 'Lithuania', 'Luxembourg', 'Malta', 'Portugal',
    'Slovakia', 'Slovenia', 'Spain'
  ];

  let currency = 'USD'; 
  let locale = 'en-US';

  if (mappings[country]) {
    currency = mappings[country].currency;
    locale = mappings[country].locale;
  } else if (euCountries.includes(country)) {
    currency = 'EUR';
    locale = 'en-IE';
  }

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    notation: "compact",
    maximumFractionDigits: 1
  }).format(val);
};

export default function ExecutiveReadout() {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  
  const [report, setReport] = useState({
    alignment: null,
    readiness: null,
    workflows: [],
    blockers: [],
    metrics: { totalFriction: 0, grossSavings: 0, netValue: 0, apiCosts: 0, cacheHits: 0, quickWins: [], strategicBets: [] }
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!user || !user.org_id) return;

      try {
        setLoading(true);
        const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const headers = { Authorization: `Bearer ${token}` };

        const [alignRes, readyRes, wfRes, progRes, fwRes] = await Promise.all([
          axios.get(`${baseURL}/api/sprint/alignment`, { headers }).catch(() => ({ data: null })),
          axios.get(`${baseURL}/api/sprint/readiness`, { headers }).catch(() => ({ data: null })),
          axios.get(`${baseURL}/api/sprint/workflow-map/${user.org_id}`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${baseURL}/api/progress`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${baseURL}/api/firewall/logs`, { headers }).catch(() => ({ data: { cacheHits: 0 } }))
        ]);

        const workflows = Array.isArray(wfRes.data) ? wfRes.data : [];
        const progressTasks = Array.isArray(progRes.data) ? progRes.data : [];
        const cacheHits = fwRes.data?.cacheHits || 0;

        const totalFriction = workflows.reduce((acc, curr) => acc + (Number(curr.fte_cost) || 0), 0);
        
        const grossSavings = workflows.filter(w => w.impact_score >= 6).reduce((acc, curr) => acc + ((Number(curr.fte_cost) || 0) * 0.4), 0);
        const cacheSavings = cacheHits * 0.02;
        const baseComputeCost = grossSavings > 0 ? (grossSavings * 0.05) : 0;
        const apiCosts = Math.max(0, baseComputeCost - cacheSavings);
        const netValue = (grossSavings + cacheSavings) - apiCosts;

        const quickWins = workflows.filter(w => w.impact_score >= 5 && w.effort_score < 5).sort((a, b) => b.impact_score - a.impact_score).slice(0, 5);
        const strategicBets = workflows.filter(w => w.impact_score >= 5 && w.effort_score >= 5).sort((a, b) => b.impact_score - a.impact_score).slice(0, 5);

        const blockers = progressTasks.filter(p => p.status !== 'Completed' && (p.item_type === 'action_plan' || p.item_type === 'cross_risk'));

        setReport({
          alignment: alignRes.data,
          readiness: readyRes.data,
          workflows,
          blockers,
          metrics: { totalFriction, grossSavings, netValue, apiCosts, cacheHits, quickWins, strategicBets }
        });
      } catch (error) {
        console.error("Failed to load readout data", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [user?.org_id, token]);

  const handlePrint = () => {
    window.print();
  };

  // Parse Heat Map Scores from the readiness JSON
  let heatMapScores = { Data: 0, Security: 0, Infrastructure: 0, Compliance: 0 };
  if (report.readiness?.data_silos) {
    try {
      const parsed = JSON.parse(report.readiness.data_silos);
      if (parsed.Data !== undefined) heatMapScores.Data = parsed.Data;
      if (parsed.Security !== undefined) heatMapScores.Security = parsed.Security;
      if (parsed.Infrastructure !== undefined) heatMapScores.Infrastructure = parsed.Infrastructure;
      if (parsed.Compliance !== undefined) heatMapScores.Compliance = parsed.Compliance;
    } catch (e) {
      console.error("Could not parse data_silos for heat map");
    }
  }

  const heatMapPillars = [
    { name: 'Data Architecture', score: heatMapScores.Data },
    { name: 'Security Posture', score: heatMapScores.Security },
    { name: 'Cloud Infrastructure', score: heatMapScores.Infrastructure },
    { name: 'Compliance & Legal', score: heatMapScores.Compliance }
  ];

  if (loading) return <div className="p-8 text-slate-500 text-center font-medium">Compiling Executive Readout...</div>;

  return (
    <>
      {/* ── EXTREME PDF EXPORT OVERRIDE ── */}
      <style>
        {`
          @media print {
            @page { margin: 10mm; size: auto; }
            
            /* Destroy the flex layout of the dashboard's sidebars/wrappers */
            html, body, #root, .App, main, div[class*="flex"] {
              display: block !important;
              margin: 0 !important;
              padding: 0 !important;
              width: 100% !important;
              position: static !important;
              overflow: visible !important;
              background-color: white !important;
            }

            /* Hide everything in the body by default */
            body * {
              visibility: hidden;
            }

            /* Resurface ONLY our Readout Zone and its descendants */
            #fcaio-executive-readout-zone, 
            #fcaio-executive-readout-zone * {
              visibility: visible;
            }

            /* Snap our Readout Zone to the top-left corner of the page */
            #fcaio-executive-readout-zone {
              position: absolute !important;
              left: 0 !important;
              top: 0 !important;
              width: 100% !important;
              max-width: 100% !important;
              margin: 0 auto !important;
              padding: 0 !important;
              background: white !important;
            }

            /* Eliminate any box shadows or borders for a clean physical print */
            #fcaio-executive-readout-zone > div {
              box-shadow: none !important;
              border: none !important;
            }

            /* Hide the export button */
            .print\\:hidden {
              display: none !important;
            }
          }
        `}
      </style>

      {/* ── WRAPPED ENTIRE PAGE IN ID FOR PDF ISOLATION ── */}
      <div id="fcaio-executive-readout-zone" className="bg-slate-100 min-h-screen pb-24 print:bg-white print:pb-0">
        
        {/* ── NON-PRINTABLE TOP BAR ── */}
        <div className="max-w-5xl mx-auto p-6 pt-8 print:hidden flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Executive Readout</h1>
            <p className="text-slate-500 mt-1">Presentation-ready briefing for Board & C-Suite stakeholders.</p>
          </div>
          <button 
            onClick={handlePrint}
            className="flex items-center gap-2 bg-slate-900 text-white px-5 py-2.5 rounded-lg shadow font-bold hover:bg-slate-800 transition-colors print:hidden"
          >
            <Printer className="w-4 h-4" /> Export to PDF
          </button>
        </div>

        {/* ── PRINTABLE DOCUMENT A4 ── */}
        <div className="max-w-4xl mx-auto bg-white shadow-xl border border-slate-200 print:shadow-none print:border-none p-12 md:p-20 print:p-0">
          
          {/* COVER SECTION */}
          <div className="border-b-4 border-slate-900 pb-8 mb-10">
            <div className="flex justify-between items-start mb-10">
              <div>
                <h3 className="text-slate-500 font-bold tracking-widest uppercase text-sm mb-2">Confidential • Board Briefing</h3>
                <h1 className="text-4xl md:text-5xl font-black text-slate-900 leading-tight">Enterprise AI Strategy<br/>& Risk Readout</h1>
              </div>
              <div className="text-right">
                <p className="text-slate-500 font-medium">Prepared for</p>
                <h2 className="text-xl font-bold text-slate-800">{user?.company_name || user?.org_name || 'Executive Team'}</h2>
                <p className="text-slate-500 text-sm mt-1">{new Date().toLocaleDateString()}</p>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-6 mt-8 bg-slate-50 p-6 rounded-2xl border border-slate-100 print:bg-slate-50">
              <div>
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 flex items-center gap-1"><DollarSign className="w-4 h-4"/> 6-Mo Net Value</p>
                <h2 className="text-3xl font-black text-emerald-600">{formatCurrency(report.metrics.netValue, user?.country)}</h2>
              </div>
              <div>
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 flex items-center gap-1"><Activity className="w-4 h-4"/> Readiness Index</p>
                <h2 className={`text-3xl font-black ${(report.readiness?.readiness_score || 0) >= 80 ? 'text-emerald-600' : 'text-amber-500'}`}>
                  {report.readiness?.readiness_score || 0} <span className="text-lg text-slate-400">/ 100</span>
                </h2>
              </div>
              <div>
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 flex items-center gap-1"><ShieldAlert className="w-4 h-4"/> Critical Risks</p>
                <h2 className={`text-3xl font-black ${report.blockers.length === 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {report.blockers.length} Active
                </h2>
              </div>
            </div>
          </div>

          {/* SECTION 1: ALIGNMENT & POSTURE */}
          <div className="mb-12 print:break-inside-avoid">
            <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
              <Target className="w-6 h-6 text-blue-600" /> Executive Alignment & Infrastructure
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div>
                  <h4 className="font-bold text-slate-800 mb-2 border-b pb-1">Core Business Objectives</h4>
                  <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">
                    {report.alignment?.business_objectives || 'No objectives recorded.'}
                  </p>
                </div>
                <div>
                  <h4 className="font-bold text-slate-800 mb-2 border-b pb-1">Executive Blockers</h4>
                  <p className="text-sm text-amber-700 leading-relaxed whitespace-pre-wrap">
                    {report.alignment?.executive_blockers || 'No blockers recorded.'}
                  </p>
                </div>
              </div>

              {/* UPGRADED INFRASTRUCTURE POSTURE HEAT MAP */}
              <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                <div className="flex items-center gap-2 mb-6">
                  <Layers className="w-5 h-5 text-indigo-600" />
                  <h3 className="text-lg font-bold text-slate-900 uppercase tracking-wide">
                    Readiness Heat Map
                  </h3>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {heatMapPillars.map((pillar, idx) => {
                    let bgColor = 'bg-slate-50';
                    let textColor = 'text-slate-600';
                    let borderColor = 'border-slate-200';
                    let statusText = 'Unknown';

                    if (pillar.score >= 80) {
                      bgColor = 'bg-emerald-50';
                      textColor = 'text-emerald-700';
                      borderColor = 'border-emerald-200';
                      statusText = 'Optimized';
                    } else if (pillar.score >= 50) {
                      bgColor = 'bg-amber-50';
                      textColor = 'text-amber-700';
                      borderColor = 'border-amber-200';
                      statusText = 'Developing';
                    } else {
                      bgColor = 'bg-red-50';
                      textColor = 'text-red-700';
                      borderColor = 'border-red-200';
                      statusText = 'Critical Risk';
                    }

                    return (
                      <div 
                        key={idx} 
                        className={`relative p-4 rounded-xl border-2 ${bgColor} ${borderColor} flex flex-col justify-between transition-all duration-300 hover:shadow-md`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <span className={`font-bold text-sm ${textColor}`}>{pillar.name}</span>
                          <span className={`text-[10px] uppercase font-black tracking-wider px-2 py-0.5 rounded-full bg-white/80 ${textColor} border ${borderColor}`}>
                            {statusText}
                          </span>
                        </div>
                        
                        <div className="flex items-end gap-1 mt-3">
                          <span className={`text-4xl font-black leading-none tracking-tighter ${textColor}`}>
                            {pillar.score}
                          </span>
                          <span className={`text-sm font-bold pb-1 opacity-60 ${textColor}`}>
                            /100
                          </span>
                        </div>

                        <div className="w-full bg-white/60 h-1.5 rounded-full mt-4 overflow-hidden">
                          <div 
                            className={`h-full rounded-full ${pillar.score >= 80 ? 'bg-emerald-500' : pillar.score >= 50 ? 'bg-amber-500' : 'bg-red-500'}`} 
                            style={{ width: `${pillar.score}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* SECTION 2: GOVERNANCE & SECURITY */}
          <div className="mb-12 print:break-inside-avoid">
            <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
              <ShieldAlert className="w-6 h-6 text-red-600" /> Security Posture & Compliance
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* The Zero Trust Gateway Proof */}
              <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-5">
                 <div className="flex items-center gap-2 mb-2">
                   <Server className="w-5 h-5 text-indigo-600" />
                   <h4 className="font-bold text-indigo-900 text-sm">FCAIO Zero-Trust Gateway</h4>
                 </div>
                 <p className="text-xs text-indigo-800 mb-3">Inline security is active. Currently processing AI inference queries to enforce corporate policy and prevent Prompt Injection.</p>
                 <div className="text-xs font-bold text-indigo-600 bg-white px-3 py-1.5 rounded-lg border border-indigo-100 inline-block">
                   {report.metrics.cacheHits} Malicious/Cached Queries Intercepted
                 </div>
              </div>

              {/* Blockers */}
              <div>
                {report.blockers.length === 0 ? (
                  <p className="text-emerald-700 font-bold bg-emerald-50 p-4 rounded-xl border border-emerald-200 text-sm">
                    <CheckCircle2 className="w-5 h-5 inline mr-1" /> All critical compliance gaps resolved.
                  </p>
                ) : (
                  <div className="bg-red-50/50 border border-red-100 rounded-xl p-4">
                    <p className="text-xs text-red-800 font-bold mb-2 uppercase tracking-wider">Mandatory Remediation:</p>
                    <ul className="space-y-2">
                      {report.blockers.slice(0, 3).map((blocker, idx) => (
                        <li key={idx} className="flex gap-2 text-xs text-slate-700 font-medium">
                          <span className="text-red-500 font-bold">⚠️</span> {blocker.item_title}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* SECTION 3: THE AI OPPORTUNITY MATRIX */}
          <div className="mb-8 print:break-inside-avoid">
            <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
              <Zap className="w-6 h-6 text-emerald-600" /> Execution Roadmap Highlights
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="font-bold text-sm text-emerald-800 mb-3 uppercase tracking-wider">Phase 1: Quick Wins</h3>
                <div className="space-y-3">
                  {report.metrics.quickWins.length === 0 ? <p className="text-sm text-slate-500">No quick wins identified.</p> : 
                    report.metrics.quickWins.slice(0,4).map((wf, idx) => (
                      <div key={idx} className="border border-slate-200 p-3 rounded-lg flex justify-between items-center bg-slate-50">
                        <div>
                          <p className="font-bold text-slate-900 text-sm">{wf.process_name}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-black text-emerald-600 text-sm">{formatCurrency((Number(wf.fte_cost) || 0) * 0.4, user?.country)}</p>
                        </div>
                      </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="font-bold text-sm text-blue-800 mb-3 uppercase tracking-wider">Phase 2: Strategic Bets</h3>
                <div className="space-y-3">
                  {report.metrics.strategicBets.length === 0 ? <p className="text-sm text-slate-500">No strategic bets identified.</p> : 
                    report.metrics.strategicBets.slice(0,4).map((wf, idx) => (
                      <div key={idx} className="border border-slate-200 p-3 rounded-lg flex justify-between items-center bg-slate-50">
                        <div>
                          <p className="font-bold text-slate-900 text-sm">{wf.process_name}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-black text-blue-600 text-sm">{formatCurrency((Number(wf.fte_cost) || 0) * 0.4, user?.country)}</p>
                        </div>
                      </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* FOOTER */}
          <div className="border-t border-slate-200 pt-6 mt-12 text-center print:block">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
              FCAIO Platform • Confidential Executive Briefing
            </p>
          </div>

        </div>
      </div>
    </>
  );
}